import React from "react";
import { DateTime } from "luxon";
import { z } from "zod/v4";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import AutoComplete from "../../utils/components/AutoCompleteComp/AutoComplete2.tsx";
import DateInput from "../../utils/components/DateInput.tsx";
import FloatInput from "../../utils/components/FloatInput.tsx";
import { filterCompte } from "../processing/compteSearcher";
import DuplicateFactureWarningModal, {
  DuplicateFactureWarningModalItem,
} from "./DuplicateFactureWarningModal.tsx";
import ImportedInvoiceReceipt from "./ImportedInvoiceReceipt.tsx";

const hostDirty = import.meta.env.VITE_DEV_HOST || window.location.host;
const modeEnv = import.meta.env.MODE;
const prefix = modeEnv === "development" ? "http://" : "https://";
const host = hostDirty.replace("http://", "");
const analyzeUrl = `${prefix}${host}/comptaRest/factures/batch/analyze`;
const commitUrl = `${prefix}${host}/comptaRest/factures/batch/commit`;

type InvoiceType = "normal" | "resto";

type InvoiceMode =
  | "Espèces"
  | "Carte1"
  | "CartePerso"
  | "Virement1"
  | "Chèque1"
  | "Prélèvement1";

type BatchItem = {
  docId: string | null;
  originalFileName: string;
  pdfUrl: string | null;
  modeRef: string | null;
  chequeNumber: string | null;
  montantCent: number | null;
  dateFacture: Date | null;
  datePrelevement: Date | null;
  montantTitresCent: number | null;
  montantFraisCent: number | null;
  error: string | null;
};

type ReadyBatchItem = BatchItem & {
  docId: string;
  modeRef: string;
  montantCent: number;
};

const paymentModes: InvoiceMode[] = [
  "Prélèvement1",
  "Virement1",
  "Carte1",
  "Chèque1",
  "Espèces",
  "CartePerso",
];

const formatDate = (date: Date | null) => {
  return date ? DateTime.fromJSDate(date).toFormat("dd/MM/yyyy") : "";
};

const serializeDateForApi = (date: Date | null) => {
  return date ? date.toISOString() : null;
};

const parseAnalyzeResponse = (raw: unknown) => {
  const data = z
    .object({
      items: z.array(
        z.object({
          docId: z.string().nullable(),
          originalFileName: z.string(),
          pdfUrl: z.string().nullable(),
          modeRef: z.string().nullable(),
          chequeNumber: z.string().nullable().optional(),
          montantCent: z.number().nullable(),
          dateFacture: z.iso.datetime().nullable(),
          datePrelevement: z.iso.datetime().nullable(),
          montantTitresCent: z.number().nullable(),
          montantFraisCent: z.number().nullable(),
          error: z.string().nullable(),
        }),
      ),
    })
    .parse(raw);
  return data.items.map((item) => ({
    ...item,
    chequeNumber: item.chequeNumber || null,
    dateFacture: item.dateFacture
      ? DateTime.fromISO(item.dateFacture).toJSDate()
      : null,
    datePrelevement: item.datePrelevement
      ? DateTime.fromISO(item.datePrelevement).toJSDate()
      : null,
  }));
};

const FactureBatchImportModule = () => {
  const { data: libellés } = trpc.libellé.getAll.useQuery();
  const utils = trpc.useUtils();

  const [emetteurSearch, setEmetteurSearch] = React.useState("");
  const [emetteur, setEmetteur] = React.useState("");
  const [invoiceType, setInvoiceType] = React.useState<InvoiceType>("normal");
  const [mode, setMode] = React.useState<InvoiceMode>("Prélèvement1");
  const [compteSearch, setCompteSearch] = React.useState("");
  const [compte, setCompte] = React.useState("");

  const [items, setItems] = React.useState<BatchItem[]>([]);
  const [receivedDocIds, setReceivedDocIds] = React.useState<string[]>([]);
  const [selectedItemIdx, setSelectedItemIdx] = React.useState(0);
  const [isAnalyzing, setIsAnalyzing] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = React.useState(false);
  const [analyzeError, setAnalyzeError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [duplicateWarningItems, setDuplicateWarningItems] = React.useState<
    DuplicateFactureWarningModalItem[]
  >([]);

  const emetteurCandidates = emetteurSearch
    ? (libellés || [])
        .map((l) => l.libellé)
        .filter((label) => new RegExp(emetteurSearch, "i").test(label))
    : [];

  const handleEmetteurChange = (value: string) => {
    const nextEmetteur = value || "";
    const matchingLibellé = libellés?.find((l) => l.libellé === nextEmetteur);
    setEmetteur(nextEmetteur);
    if (matchingLibellé) {
      setCompte(matchingLibellé.compte);
      setCompteSearch("");
    }
  };

  const compteCandidates = filterCompte(compteSearch);

  const getItemBlockingErrors = (item: BatchItem) => {
    const errors: string[] = [];
    if (!item.docId) {
      errors.push("Document introuvable");
    }
    if (!item.modeRef?.trim()) {
      errors.push("Référence facture obligatoire");
    }
    if (mode === "Chèque1" && !item.chequeNumber?.trim()) {
      errors.push("Numéro de chèque obligatoire");
    }
    if (typeof item.montantCent !== "number") {
      errors.push("Montant TTC manquant");
    }
    if (!item.dateFacture && !item.datePrelevement) {
      errors.push("Date facture ou date prélèvement obligatoire");
    }
    if (
      invoiceType === "resto" &&
      (typeof item.montantTitresCent !== "number" ||
        typeof item.montantFraisCent !== "number")
    ) {
      errors.push("Montants titres/frais obligatoires (facture resto)");
    }
    return errors;
  };

  const isItemValidForSubmit = (item: BatchItem): item is ReadyBatchItem => {
    return (
      getItemBlockingErrors(item).length === 0 &&
      item.docId !== null &&
      item.modeRef !== null &&
      item.montantCent !== null
    );
  };

  const validItems = items.filter(isItemValidForSubmit);

  const canAnalyze =
    !!emetteur &&
    (invoiceType === "resto" || (invoiceType === "normal" && !!compte));

  const canSubmit =
    canAnalyze &&
    validItems.length > 0 &&
    !isSubmitting &&
    !isAnalyzing &&
    !isCheckingDuplicates;

  const handleAnalyze = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    if (!canAnalyze) {
      setAnalyzeError(
        "Veuillez renseigner l'émetteur et le compte pour un batch normal",
      );
      return;
    }

    setAnalyzeError(null);
    setSuccessMessage(null);
    setIsAnalyzing(true);

    const formData = new FormData();
    Array.from(files).forEach((file, i) => {
      formData.append(`file-${i}`, file, file.name);
    });

    try {
      const query = new URLSearchParams({
        emetteur,
        invoiceType,
      });
      const response = await fetch(`${analyzeUrl}?${query.toString()}`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Analyse OCR en erreur");
      }
      const json: unknown = await response.json();
      const parsedItems = parseAnalyzeResponse(json);
      setItems(parsedItems);
      setReceivedDocIds((current) => [
        ...new Set([
          ...current,
          ...parsedItems.flatMap((item) => (item.docId ? [item.docId] : [])),
        ]),
      ]);
      setSelectedItemIdx(0);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur OCR";
      setAnalyzeError(message);
    } finally {
      await utils.facture.invalidate();
      setIsAnalyzing(false);
    }
  };

  const updateItem = (
    index: number,
    updater: (item: BatchItem) => BatchItem,
  ) => {
    setItems((current) =>
      current.map((item, idx) => (idx === index ? updater(item) : item)),
    );
  };

  const removeItemAtIndex = (index: number) => {
    setItems((current) => {
      const nextItems = current.filter((_, idx) => idx !== index);
      setSelectedItemIdx((previousSelectedIdx) => {
        if (nextItems.length === 0) {
          return 0;
        }
        if (previousSelectedIdx > index) {
          return previousSelectedIdx - 1;
        }
        if (previousSelectedIdx === index) {
          return Math.max(0, index - 1);
        }
        return previousSelectedIdx;
      });
      return nextItems;
    });
  };

  const selectedItem = items[selectedItemIdx] || null;

  const commitBatch = async (confirmedWarnings: DuplicateFactureWarningModalItem[] = []) => {
    const submittedItems = validItems;
    setAnalyzeError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);
    try {
      const response = await fetch(commitUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          emetteur,
          invoiceType,
          mode,
          compte: invoiceType === "normal" ? compte : null,
          items: submittedItems.map((item) => ({
            docId: item.docId,
            confirmedDuplicateKeys: confirmedWarnings
              .filter((warning) => warning.docId === item.docId)
              .flatMap((warning) => warning.duplicates.map((candidate) => `${candidate.kind}:${candidate._id}`)),
            originalFileName: item.originalFileName,
            modeRef: item.modeRef.trim(),
            chequeNumber:
              mode === "Chèque1"
                ? (item.chequeNumber || "").trim() || item.modeRef.trim()
                : null,
            montantCent: item.montantCent,
            dateFacture: serializeDateForApi(item.dateFacture),
            datePrelevement: serializeDateForApi(item.datePrelevement),
            montantTitresCent: item.montantTitresCent,
            montantFraisCent: item.montantFraisCent,
          })),
        }),
      });
      const payload: unknown = await response.json();
      if (!response.ok) {
        const error = z.object({ message: z.string() }).safeParse(payload);
        throw new Error(error.success ? error.data.message : "Erreur pendant l'enregistrement du batch");
      }
      const { insertedCount } = z
        .object({ insertedCount: z.number().int().nonnegative() })
        .parse(payload);
      if (insertedCount !== submittedItems.length) {
        throw new Error(
          "Le lot n'a pas été entièrement enregistré. Retrouvez les documents restants dans « À traiter ».",
        );
      }
      const factureLabel = insertedCount > 1 ? "factures" : "facture";
      const enregistreeLabel =
        insertedCount > 1 ? "enregistrées" : "enregistrée";
      const submittedDocIds = new Set(submittedItems.map((item) => item.docId));
      setItems((current) =>
        current.filter((item) =>
          item.docId === null || !submittedDocIds.has(item.docId),
        ),
      );
      setSelectedItemIdx(0);
      setSuccessMessage(
        `${insertedCount} ${factureLabel} ${enregistreeLabel} avec succès`,
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur pendant l'enregistrement du batch";
      setAnalyzeError(message);
    } finally {
      await Promise.all([utils.facture.invalidate(), utils.libellé.invalidate()]);
      setIsSubmitting(false);
    }
  };

  const submitBatch = async () => {
    if (!canSubmit) {
      return;
    }
    setIsCheckingDuplicates(true);
    try {
      const duplicateResults = await utils.facture.checkBusinessDuplicatesBatch.fetch({
        items: validItems.map((item) => ({
          docId: item.docId, originalFileName: item.originalFileName,
          modeRef: item.modeRef, dateFacture: item.dateFacture || item.datePrelevement,
          montantCent: item.montantCent,
        })),
      });
      const warningItems = duplicateResults.map((warning) => ({
        docId: warning.docId, title: warning.originalFileName, duplicates: warning.duplicates,
      }));

      if (warningItems.length > 0) {
        setDuplicateWarningItems(warningItems);
        return;
      }

      await commitBatch();
    } catch (error) {
      setAnalyzeError(
        error instanceof Error
          ? error.message
          : "Erreur pendant la recherche de doublons",
      );
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-header">
        <span className="fs-6">
          Importer plusieurs factures d'un même fournisseur
        </span>
      </div>
      <fieldset
        className="card-body"
        disabled={
          isAnalyzing ||
          isSubmitting ||
          isCheckingDuplicates ||
          duplicateWarningItems.length > 0
        }
      >
        <div className="row g-2 align-items-start">
          <div className="col-12 col-md-3">
            <label>Émetteur</label>
            <AutoComplete
              candidates={emetteurCandidates}
              onSetSearch={(value) => setEmetteurSearch(value || "")}
              onSetValue={handleEmetteurChange}
              value={emetteur}
              tagsOn={false}
              acceptNewValues={true}
              isValid={!!emetteur}
              invalidMessage="L'émetteur est obligatoire"
            />
          </div>
          <div className="col-12 col-md-2">
            <label>Type facture</label>
            <select
              className="form-select"
              value={invoiceType}
              onChange={(e) => setInvoiceType(e.target.value as InvoiceType)}
            >
              <option value="normal">Normale</option>
              <option value="resto">Resto</option>
            </select>
          </div>
          <div className="col-12 col-md-2">
            <label>Mode paiement</label>
            <select
              className="form-select"
              value={mode}
              onChange={(e) => {
                const nextMode = e.target.value as InvoiceMode;
                setMode(nextMode);
                if (nextMode === "Chèque1") {
                  setItems((current) =>
                    current.map((item) => ({
                      ...item,
                      chequeNumber:
                        item.chequeNumber || item.modeRef?.trim() || null,
                    })),
                  );
                }
              }}
            >
              {paymentModes.map((modeChoice) => (
                <option key={modeChoice} value={modeChoice}>
                  {modeChoice}
                </option>
              ))}
            </select>
          </div>
          {invoiceType === "normal" && (
            <div className="col-12 col-md-3">
              <label>Compte comptable</label>
              <AutoComplete
                candidates={compteCandidates}
                onSetSearch={(value) => setCompteSearch(value || "")}
                onSetValue={(value) => setCompte(value || "")}
                value={compte}
                tagsOn={false}
                acceptNewValues={false}
                isValid={!!compte}
                invalidMessage="Le compte est obligatoire"
              />
            </div>
          )}
          <div className="col-12 col-md-2">
            <label>PDF batch</label>
            <input
              type="file"
              multiple
              accept="application/pdf"
              className="form-control"
              disabled={isAnalyzing || isSubmitting || isCheckingDuplicates}
              onChange={(event) => {
                handleAnalyze(event.currentTarget.files);
                event.currentTarget.value = "";
              }}
            />
          </div>
        </div>

        {analyzeError && (
          <div className="alert alert-danger mt-2" role="alert">
            {analyzeError}
          </div>
        )}
        {isAnalyzing && (
          <div className="alert alert-info mt-2" role="alert">
            Analyse OCR en cours...
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success mt-2" role="alert">
            {successMessage}
          </div>
        )}
        <ImportedInvoiceReceipt documentIds={receivedDocIds} />

        {items.length > 0 && (
          <>
            <div className="table-responsive mt-3">
              <table className="table table-sm table-striped table-hover">
                <thead>
                  <tr>
                    <th>Fichier</th>
                    <th>Référence</th>
                    {mode === "Chèque1" && <th>N° chèque</th>}
                    <th>Montant TTC</th>
                    <th>Date facture</th>
                    <th>Date prélèvement</th>
                    {invoiceType === "resto" && <th>Montant titres</th>}
                    {invoiceType === "resto" && <th>Montant frais</th>}
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const blockingErrors = getItemBlockingErrors(item);
                    const hasBlockingErrors = blockingErrors.length > 0;
                    const hadOcrError = !!item.error;
                    return (
                      <tr
                        key={`${item.originalFileName}-${idx}`}
                        className={
                          selectedItemIdx === idx ? "table-primary" : ""
                        }
                        style={{ cursor: "pointer" }}
                        title="Cliquer pour afficher ce document"
                        onClick={() => setSelectedItemIdx(idx)}
                      >
                        <td>{item.originalFileName}</td>
                        <td style={{ minWidth: 180 }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.modeRef || ""}
                            onChange={(event) =>
                              updateItem(idx, (old) => ({
                                ...old,
                                modeRef: event.target.value,
                              }))
                            }
                            placeholder="Référence"
                          />
                        </td>
                        {mode === "Chèque1" && (
                          <td style={{ minWidth: 160 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.chequeNumber || ""}
                              onChange={(event) =>
                                updateItem(idx, (old) => ({
                                  ...old,
                                  chequeNumber: event.target.value,
                                }))
                              }
                              placeholder="Numéro de chèque"
                            />
                          </td>
                        )}
                        <td style={{ minWidth: 130 }}>
                          <FloatInput
                            valueCent={item.montantCent}
                            onValueCentChange={(valueCent) =>
                              updateItem(idx, (old) => ({
                                ...old,
                                montantCent: valueCent,
                              }))
                            }
                          />
                        </td>
                        <td style={{ minWidth: 130 }}>
                          <DateInput
                            date={item.dateFacture}
                            onDateChange={(dateFacture) =>
                              updateItem(idx, (old) => ({
                                ...old,
                                dateFacture,
                              }))
                            }
                          />
                        </td>
                        <td style={{ minWidth: 130 }}>
                          <DateInput
                            date={item.datePrelevement}
                            onDateChange={(datePrelevement) =>
                              updateItem(idx, (old) => ({
                                ...old,
                                datePrelevement,
                              }))
                            }
                          />
                        </td>
                        {invoiceType === "resto" && (
                          <td style={{ minWidth: 130 }}>
                            <FloatInput
                              valueCent={item.montantTitresCent}
                              onValueCentChange={(montantTitresCent) =>
                                updateItem(idx, (old) => ({
                                  ...old,
                                  montantTitresCent,
                                }))
                              }
                            />
                          </td>
                        )}
                        {invoiceType === "resto" && (
                          <td style={{ minWidth: 130 }}>
                            <FloatInput
                              valueCent={item.montantFraisCent}
                              onValueCentChange={(montantFraisCent) =>
                                updateItem(idx, (old) => ({
                                  ...old,
                                  montantFraisCent,
                                }))
                              }
                            />
                          </td>
                        )}
                        <td>
                          {hasBlockingErrors ? (
                            <span className="badge text-bg-danger">
                              À corriger
                            </span>
                          ) : hadOcrError ? (
                            <span className="badge text-bg-warning">
                              Corrigée
                            </span>
                          ) : (
                            <span className="badge text-bg-success">OK</span>
                          )}
                          {hadOcrError && (
                            <div className="small text-warning-emphasis mt-1">
                              OCR: {item.error}
                            </div>
                          )}
                          {hasBlockingErrors && (
                            <ul className="small text-danger mb-0 mt-1 ps-3">
                              {blockingErrors.map((error) => (
                                <li
                                  key={`${item.originalFileName}-${idx}-${error}`}
                                >
                                  {error}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              removeItemAtIndex(idx);
                            }}
                            disabled={isSubmitting}
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {selectedItem && (
              <div className="mt-2">
                <div className="mb-1">
                  <strong>Viewer:</strong> {selectedItem.originalFileName} — TTC{" "}
                  {selectedItem.montantCent ?? "?"} — Date facture{" "}
                  {formatDate(selectedItem.dateFacture)} — Date
                  prélèvement/réglement{" "}
                  {formatDate(selectedItem.datePrelevement)} — Référence{" "}
                  {selectedItem.modeRef || "?"}
                  {mode === "Chèque1"
                    ? ` — N° chèque ${selectedItem.chequeNumber || "?"}`
                    : ""}
                </div>
                {selectedItem.pdfUrl ? (
                  <iframe
                    src={selectedItem.pdfUrl}
                    title="Facture PDF"
                    style={{
                      width: "100%",
                      height: 520,
                      border: "1px solid #ddd",
                    }}
                  />
                ) : (
                  <div className="alert alert-warning">
                    Aucun PDF disponible pour la prévisualisation
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canSubmit}
                onClick={submitBatch}
              >
                {isCheckingDuplicates
                  ? "Vérification des doublons..."
                  : isSubmitting
                    ? "Enregistrement..."
                    : `Valider et enregistrer ${validItems.length} facture(s)`}
              </button>
            </div>
          </>
        )}
        <DuplicateFactureWarningModal
          show={duplicateWarningItems.length > 0}
          items={duplicateWarningItems}
          onCancel={() => setDuplicateWarningItems([])}
          onConfirm={async () => {
            setDuplicateWarningItems([]);
            await commitBatch(duplicateWarningItems);
          }}
          confirmLabel="Enregistrer le batch quand même"
          isSubmitting={isSubmitting}
        />
      </fieldset>
    </div>
  );
};

export default FactureBatchImportModule;
